import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { MessageDescriptor } from 'react-intl';
import type { Music24Capabilities } from 'fym-common/src/license/music24Plans';
import { faChartMixed } from '@fortawesome/pro-light-svg-icons/faChartMixed';
import { faArrowTrendUp } from '@fortawesome/pro-light-svg-icons/faArrowTrendUp';
import { faAlbumCollection } from '@fortawesome/pro-light-svg-icons/faAlbumCollection';
import { faChartSimple } from '@fortawesome/pro-light-svg-icons/faChartSimple';
import { faWandMagicSparkles } from '@fortawesome/pro-light-svg-icons/faWandMagicSparkles';
import { faImages } from '@fortawesome/pro-light-svg-icons/faImages';
import { faLink } from '@fortawesome/pro-light-svg-icons/faLink';
import { faGear } from '@fortawesome/pro-light-svg-icons/faGear';
import { faCompactDisc } from '@fortawesome/pro-light-svg-icons/faCompactDisc';
import { faBolt } from '@fortawesome/pro-light-svg-icons/faBolt';
import { faEye } from '@fortawesome/pro-light-svg-icons/faEye';
import { faPaperPlane } from '@fortawesome/pro-light-svg-icons/faPaperPlane';
import { faInbox } from '@fortawesome/pro-light-svg-icons/faInbox';
import { faShareNodes } from '@fortawesome/pro-light-svg-icons/faShareNodes';
import { SMLRouteNames, SMLRouteParts, SMLRoutes } from '../../../routing/sml/SMLRoutes';
import {
  smlNavigationDescriptionMessages as descriptions,
  smlNavigationGroupMessages as groupMessages,
  smlNavigationMessages as messages,
} from './smlNavigationMessages';

export type SMLNavGroupId = 'public' | 'yours' | 'promote' | 'outreach';

/**
 * Where a menu badge reads its number. Navigation v2 (owner, 2026-10-23): a menu badge is a red
 * count of things that wait for the person, or the Catalog setup dot. `kitWork` counts only the
 * failed files (the "working" state is a chip on Studio, not a menu badge). `outreachInbox` is the
 * "Needs you" count of the Inbox. The channels that need a new sign-in are not an item badge: the
 * account card reads them (`SMLNavBadges.accountAttention`).
 */
export type SMLNavBadgeSource = 'failedPosts' | 'catalogSetup' | 'kitWork' | 'outreachInbox';

/** The plan limit a hub tile captions (design §6). The sidebar never shows it. */
export type SMLNavUsageKey = 'portalHistory' | 'pages' | 'scheduledPosts' | 'videoCredits' | 'socialAccounts';

/** A capability that must be true before the navigation shows an item. */
export type SMLNavCapability = 'canUseAiReels';

export interface SMLNavItem {
  id: string;
  icon: IconDefinition;
  label: MessageDescriptor;
  /** The tile text in the mobile hub sheet. */
  description?: MessageDescriptor;
  route: SMLRouteParts;
  /** The route names that light this item. The last part of the current route is matched. */
  activeRoutes: SMLRouteNames[];
  /**
   * The route names that are tabs of the item's page (Insights → Streaming, Social, Fans). A tab is
   * not a page under the item: it has no back link. Every other active route that is not the item
   * route is a page under the item (`isDeepScreen`).
   */
  tabRoutes?: SMLRouteNames[];
  badge?: SMLNavBadgeSource;
  usage?: SMLNavUsageKey;
  /** The item is hidden while this capability of the active workspace is false. */
  capability?: SMLNavCapability;
  /** Other names the command palette finds the item by (a tab or an old name of the page). */
  aliases?: MessageDescriptor[];
}

export interface SMLNavGroup {
  id: SMLNavGroupId;
  /** Sidebar heading and breadcrumb: "Your music". */
  label: MessageDescriptor;
  /** Mobile bottom bar: "Music". */
  tabLabel: MessageDescriptor;
  /** One line under the hub sheet title. */
  description: MessageDescriptor;
  /** The bottom bar icon. */
  icon: IconDefinition;
  items: SMLNavItem[];
}

export const smlHomeNavItem: SMLNavItem = {
  id: 'home',
  icon: faChartMixed,
  label: messages.home,
  route: SMLRoutes.home,
  activeRoutes: [SMLRouteNames.home],
};

/** Settings is not drawn in the sidebar (it is in the account menu). The command palette finds it. */
export const smlSettingsNavItem: SMLNavItem = {
  id: 'settings',
  icon: faGear,
  label: messages.settings,
  route: SMLRoutes.settings,
  activeRoutes: [SMLRouteNames.settings],
};

/**
 * The one list of the Music24 web navigation: the groups, their items in order, the routes, the
 * icons, the labels and the badge and usage sources. The desktop sidebar, the collapsed rail, the
 * mobile bottom bar and hub sheets, the command palette and the breadcrumbs all read it.
 *
 * Navigation v2 (owner decisions 2026-10-23, design `navigation-v2/`): 11 items instead of 25.
 * Sibling pages are tabs of one item (Insights, Posts, Pitch, Charts). Channels moves to
 * Settings → Connections, Browse becomes the Search page behind the command palette, and Work
 * becomes a chip and a panel on Studio. No route changes: every old page is a tab or a page under
 * an item, so every deep link keeps working and lights the right item.
 */
export const smlNavGroups: SMLNavGroup[] = [
  {
    id: 'yours',
    label: groupMessages.yoursName,
    tabLabel: groupMessages.yoursTab,
    description: groupMessages.yoursDescription,
    icon: faCompactDisc,
    items: [
      {
        id: 'catalog',
        icon: faAlbumCollection,
        label: messages.catalog,
        description: descriptions.catalog,
        route: SMLRoutes.catalog,
        activeRoutes: [SMLRouteNames.catalog, SMLRouteNames.catalogLyrics],
        badge: 'catalogSetup',
      },
      {
        // design: navigation-v2 b. Tabs Streaming, Social and Fans. The Streaming sections and a
        // track (`/streaming/track/:id`) and a post (`/social/post/:id`) keep the screen of their tab.
        id: 'insights',
        icon: faChartSimple,
        label: messages.insightsItem,
        description: descriptions.insights,
        route: SMLRoutes.streaming,
        activeRoutes: [SMLRouteNames.streaming, SMLRouteNames.social, SMLRouteNames.fans],
        tabRoutes: [SMLRouteNames.streaming, SMLRouteNames.social, SMLRouteNames.fans],
        usage: 'portalHistory',
        aliases: [messages.streaming, messages.social, messages.fans, messages.insights, messages.promotePerformance],
      },
    ],
  },
  {
    id: 'promote',
    label: groupMessages.promoteName,
    tabLabel: groupMessages.promoteTab,
    description: groupMessages.promoteDescription,
    icon: faBolt,
    items: [
      {
        // design: release kit m1. Releases is the list of songs; a song page sits below it.
        id: 'promoteReleases',
        icon: faCompactDisc,
        label: messages.promoteReleases,
        description: descriptions.promoteReleases,
        route: SMLRoutes.promote,
        activeRoutes: [SMLRouteNames.promote, SMLRouteNames.promoteRelease],
      },
      {
        // design: navigation-v2 c 3e. Studio → Profile is a page under Studio. Work is the panel of
        // Studio; its route opens Studio with the panel open. The red count is the failed work.
        id: 'promoteStudio',
        icon: faWandMagicSparkles,
        label: messages.promoteStudio,
        description: descriptions.promoteStudio,
        route: SMLRoutes.promoteStudio,
        activeRoutes: [SMLRouteNames.promoteStudio, SMLRouteNames.promoteProfile, SMLRouteNames.promoteWork],
        tabRoutes: [SMLRouteNames.promoteStudio, SMLRouteNames.promoteWork],
        badge: 'kitWork',
        usage: 'videoCredits',
        aliases: [messages.promoteWork],
      },
      {
        // design: navigation-v2 c, variant B. Tabs Board and Calendar; the Composer is a page under
        // Posts. The red count is the posts that failed to publish (it was on Calendar).
        id: 'promotePosts',
        icon: faImages,
        label: messages.promotePosts,
        description: descriptions.promotePosts,
        route: SMLRoutes.promotePosts,
        activeRoutes: [SMLRouteNames.promotePosts, SMLRouteNames.promoteCalendar, SMLRouteNames.promoteComposer],
        tabRoutes: [SMLRouteNames.promotePosts, SMLRouteNames.promoteCalendar],
        badge: 'failedPosts',
        usage: 'scheduledPosts',
        aliases: [messages.promoteCalendar, messages.promoteComposer, messages.promoteAssets],
      },
      {
        // QA finding F11 (2026-10-24): the social channels are the step that makes posting work, so
        // they get a menu item again. Settings → Connections opens the same page.
        id: 'promoteChannels',
        icon: faShareNodes,
        label: messages.promoteChannels,
        description: descriptions.promoteChannels,
        route: SMLRoutes.promoteChannels,
        activeRoutes: [SMLRouteNames.promoteChannels],
        aliases: [messages.connections],
      },
      {
        id: 'smartlinks',
        icon: faLink,
        label: messages.smartlinks,
        description: descriptions.smartlinks,
        route: SMLRoutes.smartlinks,
        activeRoutes: [
          SMLRouteNames.smartlinks,
          SMLRouteNames.create,
          SMLRouteNames.details,
          SMLRouteNames.manage,
          SMLRouteNames.edit,
          SMLRouteNames.duplicate,
          SMLRouteNames.widgetEdit,
          SMLRouteNames.releaseLink,
          SMLRouteNames.playlistLink,
          SMLRouteNames.presaveLink,
        ],
        usage: 'pages',
      },
    ],
  },
  {
    // design: navigation-v2 d. Pitch has the tabs Campaigns, Contacts, Find and Community spots.
    // A campaign, its review, the campaign setup and Your sound are pages under Pitch.
    id: 'outreach',
    label: groupMessages.outreachName,
    tabLabel: groupMessages.outreachTab,
    description: groupMessages.outreachDescription,
    icon: faPaperPlane,
    items: [
      {
        id: 'outreachPitch',
        icon: faPaperPlane,
        label: messages.outreachPitch,
        description: descriptions.outreachPitch,
        route: SMLRoutes.outreach,
        activeRoutes: [
          SMLRouteNames.outreach,
          SMLRouteNames.outreachProfile,
          SMLRouteNames.outreachCampaignNew,
          SMLRouteNames.outreachCampaign,
          SMLRouteNames.outreachReview,
          SMLRouteNames.outreachResults,
          SMLRouteNames.outreachFind,
          SMLRouteNames.outreachCommunities,
          SMLRouteNames.outreachContacts,
        ],
        tabRoutes: [
          SMLRouteNames.outreach,
          SMLRouteNames.outreachContacts,
          SMLRouteNames.outreachFind,
          SMLRouteNames.outreachCommunities,
        ],
        aliases: [messages.outreachFind, messages.outreachContacts, messages.outreachCommunities],
      },
      {
        id: 'outreachInbox',
        icon: faInbox,
        label: messages.outreachInbox,
        description: descriptions.outreachInbox,
        route: SMLRoutes.outreachInbox,
        activeRoutes: [SMLRouteNames.outreachInbox],
        badge: 'outreachInbox',
      },
    ],
  },
  {
    id: 'public',
    label: groupMessages.publicName,
    tabLabel: groupMessages.publicTab,
    description: groupMessages.publicDescription,
    // design: navigation-v2 g 7a, 7g: the Discover tab shows the eye of Watchlist.
    icon: faEye,
    items: [
      {
        id: 'watchlist',
        icon: faEye,
        label: messages.watchlist,
        description: descriptions.watchlist,
        route: SMLRoutes.watchlist,
        activeRoutes: [SMLRouteNames.watchlist],
      },
      {
        // design: navigation-v2 e. Tabs Trends, Artists, Tracks, Playlists and Markets. An artist, a
        // track and a playlist are pages under Charts.
        id: 'charts',
        icon: faArrowTrendUp,
        label: messages.charts,
        description: descriptions.charts,
        route: SMLRoutes.trends,
        activeRoutes: [
          SMLRouteNames.trends,
          SMLRouteNames.artists,
          SMLRouteNames.artistDetails,
          SMLRouteNames.tracks,
          SMLRouteNames.trackDetails,
          SMLRouteNames.playlists,
          SMLRouteNames.playlistDetails,
          SMLRouteNames.markets,
        ],
        tabRoutes: [
          SMLRouteNames.trends,
          SMLRouteNames.artists,
          SMLRouteNames.tracks,
          SMLRouteNames.playlists,
          SMLRouteNames.markets,
        ],
        aliases: [messages.trends, messages.artists, messages.tracks, messages.playlists, messages.markets],
      },
    ],
  },
];

/**
 * Every destination in navigation order: Home, the groups (Your music, Promote, Outreach, Discover),
 * then Settings. 12 entries; the sidebar draws all but Settings.
 */
export const smlNavigationItems: SMLNavItem[] = [
  smlHomeNavItem,
  ...smlNavGroups.flatMap((group) => group.items),
  smlSettingsNavItem,
];

/** False when the item needs a capability the active workspace does not have. */
export function isNavItemVisible(item: SMLNavItem, capabilities: Pick<Music24Capabilities, SMLNavCapability>): boolean {
  return item.capability === undefined || capabilities[item.capability];
}

/**
 * The groups with only the items the active workspace may see, and without a group that has none
 * left. `smlNavGroups` keeps every item, so a hidden page still finds its group and breadcrumb
 * when a link opens it.
 */
export function visibleNavGroups(capabilities: Pick<Music24Capabilities, SMLNavCapability>): SMLNavGroup[] {
  // A group with no visible item goes too (Outreach without access).
  return smlNavGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => isNavItemVisible(item, capabilities)),
    }))
    .filter((group) => group.items.length > 0);
}

/** `smlNavigationItems` with only the items the active workspace may see. */
export function visibleNavigationItems(capabilities: Pick<Music24Capabilities, SMLNavCapability>): SMLNavItem[] {
  return smlNavigationItems.filter((item) => isNavItemVisible(item, capabilities));
}

/**
 * The screen of a route is its last part; the first is its section. Matching on the last part
 * keeps sibling items (Studio and Calendar, both under `promote/`) from lighting together.
 */
export function currentScreenName(route: SMLRouteParts | undefined): SMLRouteNames | undefined {
  if (route === undefined || route.length === 0) {
    return undefined;
  }
  return route[route.length - 1];
}

export function isNavItemActive(item: SMLNavItem, screen: SMLRouteNames | undefined): boolean {
  return screen !== undefined && item.activeRoutes.includes(screen);
}

/** The group that holds the page on screen, or `undefined` for Home, Settings and other pages. */
export function findNavGroupForScreen(screen: SMLRouteNames | undefined): SMLNavGroup | undefined {
  return smlNavGroups.find((group) => group.items.some((item) => isNavItemActive(item, screen)));
}

/** The destination that owns the page on screen: its own page, or a page below it. */
export function findNavItemForScreen(screen: SMLRouteNames | undefined): SMLNavItem | undefined {
  return smlNavigationItems.find((item) => isNavItemActive(item, screen));
}

/**
 * A page below a destination (a track, one smartlink, the Composer). Design b4 gives it the back +
 * title top bar. The destination's own page and its tabs (`tabRoutes`) are not deep.
 */
export function isDeepScreen(item: SMLNavItem, screen: SMLRouteNames | undefined): boolean {
  if (screen === undefined || item.route[item.route.length - 1] === screen) {
    return false;
  }
  return item.tabRoutes === undefined || !item.tabRoutes.includes(screen);
}

/**
 * design: navigation i1/i2, navigation-v2 1e. The data of a Your music, Promote or Outreach page
 * belongs to the active artist, so its page bar can show the artist switcher. A Discover page does
 * not.
 */
export function isArtistGroup(group: SMLNavGroup | undefined): boolean {
  // The Outreach pages show the data of the active artist too (design outreach a1).
  return group?.id === 'yours' || group?.id === 'promote' || group?.id === 'outreach';
}

/**
 * The page bar shows the artist switcher on every page but a Discover page (owner, 2026-10-26).
 * Home and Settings show it too: Home shows the data of one artist.
 */
export function showsArtistPill(group: SMLNavGroup | undefined): boolean {
  return group?.id !== 'public';
}

/**
 * design: navigation i1. The destination to go back to when the artist changes on a page below a
 * Your music, Promote or Outreach destination (one smartlink, the Composer, one campaign). The item
 * on that page belongs to the old artist. `undefined` for every other page, tabs included.
 */
export function findArtistItemParent(screen: SMLRouteNames | undefined): SMLNavItem | undefined {
  if (!isArtistGroup(findNavGroupForScreen(screen))) {
    return undefined;
  }
  const item = findNavItemForScreen(screen);
  return item !== undefined && isDeepScreen(item, screen) ? item : undefined;
}
